/* eslint-disable @typescript-eslint/naming-convention */
import { useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'
import { api } from '../../../../../../../utils/apiUtils'
import briefR from '../../../../../../../mobx/brief'
import userMobx from '../../../../../../../mobx/user'
import { BrandI, ClientI, ProductsBrand } from '../../../../../../../types-project/Client'
import pricingAlgorithmLegacy from '../PricingAlgorithm'
import styles from './smartPricing.module.scss'

type PriceTier = {
  label: 'low' | 'mid' | 'high'
  max: number | null
  expert_rate: number | null
  avg_applicants: number
  sample: number
}

type AudienceSupply = {
  active_creators_30d: number | null
  eligible_pool: number | null
  active_ratio: number | null
  level: 'healthy' | 'moderate' | 'tight' | 'critical' | 'unknown'
  uplift_pct: number
  uplift_mult: number
}

type AudienceSuggestion = {
  label: string
  patch: { gender?: string; age_min?: number; age_max?: number }
  kind?: 'age' | 'gender'
  active_creators_30d: number
  eligible_pool: number
  delta_active: number
  delta_pool: number
  expected_applicants?: number
  delta_applicants?: number
}

type ProjectionBase = {
  expert_rate_at_reco: number
  applicants_at_reco: number
  response_time_at_reco_h: number
}

type PriceRecommendation = {
  recommended: number
  min: number
  max: number
  floor: number
  premium_threshold: number
  confidence: number
  sample_size: number
  tension: 'balanced' | 'high-supply' | 'low-supply' | 'unknown'
  avg_applicants?: number
  expert_acquisition_rate?: number | null
  match_level?: number
  price_tiers?: PriceTier[] | null
  audience_supply?: AudienceSupply
  audience_suggestions?: AudienceSuggestion[]
  projection_base?: ProjectionBase
  fallback: boolean
  reasons: string[]
}

type MarketPulse = {
  active_count: number
  median: number | null
  min: number | null
  max: number | null
  total_applicants: number
}

type Props = {
  value: number | string | undefined
  onChange: (v: number | string) => void
  onMinimumChange?: (v: number) => void
  selectedProduct?: ProductsBrand
}

const useDebounced = <T,>(value: T, delay = 220): T => {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

function SmartPricingWidget({ value, onChange, onMinimumChange, selectedProduct }: Props) {
  const { t } = useTranslation()
  const userInfos = userMobx.getUserInfos() as ClientI | undefined
  const brandSession = userMobx.getBrandSession() as BrandI | undefined

  const brief = briefR.brief.data

  // /!\ MobX mute brief en place : on lit chaque sous-champ directement au render
  // pour que `observer` track la dépendance fine et déclenche un re-render réel.
  const content_type = brief?.content_type ?? 'ugc'
  const gender = (brief?.targeting?.gender as string) ?? 'none'
  const age_min = brief?.targeting?.age?.min ?? null
  const age_max = brief?.targeting?.age?.max ?? null
  const property_rights = brief?.property_rights ?? null
  const with_product = brief?.with_product ?? null
  const content_usage = brief?.content_usage ?? null
  const on_spot = Boolean(brief?.on_spot?.is_on_spot)
  const length_max = (brief as any)?.length_max ?? null
  const dotation = brief?.on_spot?.dotation ?? 0
  const maxApplicants = (brief as any)?.max_applicants && (brief as any).max_applicants > 0
    ? (brief as any).max_applicants
    : 20

  const inputParams = useMemo(() => {
    const brand =
      userInfos?._company?.type === 'agency'
        ? brandSession
        : (userInfos?.brand as BrandI | undefined)
    const brandCategories = Array.isArray(brand?.categories) ? brand!.categories : []
    const category_ids = brandCategories
      .map((cat: any) => (typeof cat === 'string' ? cat : cat?._id))
      .filter(Boolean)

    const product_value = selectedProduct?.price ?? dotation ?? 0

    return {
      content_type,
      gender,
      age_min,
      age_max,
      category_ids,
      property_rights,
      with_product,
      content_usage,
      on_spot,
      length_max,
      product_value,
    }
  }, [
    content_type,
    gender,
    age_min,
    age_max,
    property_rights,
    with_product,
    content_usage,
    on_spot,
    length_max,
    dotation,
    selectedProduct,
    userInfos,
    brandSession,
  ])

  const debouncedKey = useDebounced(JSON.stringify(inputParams), 500)

  const [reco, setReco] = useState<PriceRecommendation | null>(null)
  const [pulse, setPulse] = useState<MarketPulse | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const requestIdRef = useRef(0)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const currentId = ++requestIdRef.current

    const run = async () => {
      if (isMountedRef.current) {
        setLoading(true)
        setPulse(null)
      }
      try {
        const [recoRes, pulseRes] = await Promise.all([
          api.post('briefs/price-recommendation', inputParams),
          api.post('briefs/market-pulse', {
            content_type: inputParams.content_type,
            gender: inputParams.gender,
            age_min: inputParams.age_min,
            age_max: inputParams.age_max,
            category_ids: inputParams.category_ids,
          }),
        ])
        if (!isMountedRef.current || currentId !== requestIdRef.current) return
        setReco(recoRes.data)
        setPulse(pulseRes.data)
        onMinimumChange?.(recoRes.data.floor ?? 100)
      } catch (err) {
        if (!isMountedRef.current || currentId !== requestIdRef.current) return
        const legacy = pricingAlgorithmLegacy(selectedProduct)
        const fb: PriceRecommendation = {
          recommended: legacy.updatedPrice,
          min: legacy.minimumPrice,
          max: Math.round((legacy.updatedPrice * 1.4) / 5) * 5,
          floor: legacy.minimumPrice,
          premium_threshold: Math.round((legacy.updatedPrice * 1.35) / 5) * 5,
          confidence: 0,
          sample_size: 0,
          tension: 'unknown',
          fallback: true,
          reasons: [t('form-casting:casting.cards-remuneration.smart.fallback', 'Estimation basée sur les règles Vogz.')],
        }
        setReco(fb)
        setPulse(null)
        onMinimumChange?.(fb.floor)
      } finally {
        if (isMountedRef.current && currentId === requestIdRef.current) setLoading(false)
      }
    }

    run()
  }, [debouncedKey])

  const numericValue = typeof value === 'string' ? Number(value) || 0 : (value ?? 0)
  const hasValue = numericValue > 0

  const sliderRange = useMemo(() => {
    if (!reco) return { min: 80, max: 300, step: 5 }
    const pmin = Math.max(50, Math.round(reco.min * 0.85))
    const pmax = Math.round(Math.max(reco.max, reco.premium_threshold) * 1.2)
    return { min: pmin, max: pmax, step: 5 }
  }, [reco])

  const belowFloor = reco && hasValue && numericValue < reco.floor

  const handleAudienceExpand = (suggestion: AudienceSuggestion) => {
    const current = brief
    if (!current?._id) return
    const patch = suggestion.patch
    const currentTargeting: any = current.targeting ?? {}
    const nextTargeting: any = {
      ...currentTargeting,
      gender: patch.gender ?? currentTargeting.gender,
      age: {
        ...(currentTargeting.age ?? {}),
        ...(patch.age_min != null ? { min: patch.age_min } : {}),
        ...(patch.age_max != null ? { max: patch.age_max } : {}),
      },
    }
    // Mise à jour optimiste immédiate des KPI : on injecte la projection
    // déjà connue de la suggestion (calculée côté backend) sans attendre le
    // round-trip API, pour donner un feedback instantané au user.
    if (suggestion.expected_applicants != null && reco) {
      setReco({
        ...reco,
        audience_supply: reco.audience_supply
          ? {
              ...reco.audience_supply,
              active_creators_30d: suggestion.active_creators_30d,
              eligible_pool: suggestion.eligible_pool,
            }
          : reco.audience_supply,
        projection_base: reco.projection_base
          ? { ...reco.projection_base, applicants_at_reco: suggestion.expected_applicants }
          : { expert_rate_at_reco: 70, applicants_at_reco: suggestion.expected_applicants, response_time_at_reco_h: 24 },
      })
    }
    briefR.patchBrief({ targeting: nextTargeting } as any, current._id)
  }

  const baseApplicants = reco?.projection_base?.applicants_at_reco ?? reco?.avg_applicants ?? 6

  // NB : on garde la `recommended` brute du backend comme référence de projection
  // (elle correspond à `applicants_at_reco`). Le `smartReco` (ci-dessous) est juste
  // un "label de cible" qu'on affiche, pas la base mathématique de la courbe.
  const projectAtBudget = (budget: number) => {
    if (!reco || budget <= 0) {
      return { applicants: baseApplicants, boostRatio: 0 }
    }
    const ref = reco.recommended || 1
    const rawDelta = (budget - ref) / ref
    const delta = Math.max(-0.5, Math.min(1.0, rawDelta))

    const applicantsMult = delta >= 0
      ? 1 + 0.55 * (1 - Math.exp(-1.4 * delta))
      : Math.max(0.45, 1 + 0.75 * delta)

    const safeBase = Math.max(1, Number.isFinite(baseApplicants) ? baseApplicants : 6)
    const projected = Math.round(safeBase * Math.max(0, applicantsMult))
    return {
      applicants: Math.max(1, projected),
      boostRatio: delta,
    }
  }

  // ---- Smart Reco : meilleur prix utile pour la marque ----
  // Définition : prix qui permet de remplir le casting à 100 %, sans jamais
  // descendre sous la médiane marché (sinon on attire les profils en bout de
  // course). Si le vivier plafonne, on garde au moins l'alignement marché.
  const TARGET_COMPLETION_PCT = 100

  const marketMedianRaw = pulse?.median ?? null

  // Borne haute de la courbe de projection (≈ +55 % à très haut budget).
  const maxAchievableApplicants = Math.round(baseApplicants * 1.55)
  const maxAchievablePct = Math.min(100, Math.round((maxAchievableApplicants / maxApplicants) * 100))
  // Si même en payant beaucoup on ne dépasse pas ~65 %, c'est l'audience qui plafonne.
  const supplyCapped = !!reco && maxAchievablePct < 65

  const smartReco = useMemo(() => {
    if (!reco) return 0

    const marketFloor = marketMedianRaw ? Math.round(marketMedianRaw / 5) * 5 : 0

    // Vivier plafonné : on ne peut pas remplir à 100 %, on s'aligne au moins
    // sur la médiane marché pour la qualité des profils.
    if (supplyCapped) {
      return Math.max(reco.recommended, marketFloor)
    }

    const target = Math.ceil((TARGET_COMPLETION_PCT / 100) * maxApplicants)
    const ceiling = Math.max(reco.premium_threshold || 0, reco.recommended * 1.8)

    // Cherche le plus petit prix qui atteint 100 % de complétion.
    let priceFor100 = reco.recommended
    let lastApplicants = projectAtBudget(reco.recommended).applicants
    while (priceFor100 < ceiling) {
      if (lastApplicants >= target) break
      const next = priceFor100 + 5
      const projected = projectAtBudget(next).applicants
      // Saturation : la courbe ne décolle plus, inutile de continuer.
      if (projected - lastApplicants < 1 && next - reco.recommended >= 25) break
      lastApplicants = projected
      priceFor100 = next
    }
    priceFor100 = Math.round(priceFor100 / 5) * 5

    return Math.max(priceFor100, marketFloor)
  }, [reco, supplyCapped, maxApplicants, baseApplicants, marketMedianRaw])

  const projectedPrice = hasValue ? numericValue : (smartReco || reco?.recommended || 0)
  const currentProjection = reco ? projectAtBudget(projectedPrice) : null
  const projectedApplicants = currentProjection?.applicants ?? baseApplicants

  // Taux de complétion attendu (sur le cap du brief, 20 par défaut).
  // Plafonné à 100 % parce qu'au-delà du cap les candidats ne peuvent plus postuler.
  const completionPct = Math.max(0, Math.min(100, Math.round((projectedApplicants / maxApplicants) * 100)))

  const completionTone =
    completionPct >= 100 ? 'full' as const
    : completionPct >= 80 ? 'good' as const
    : completionPct >= 50 ? 'mid' as const
    : 'low' as const

  const completionLabel =
    completionPct >= 100 ? `Casting complet attendu sur ${maxApplicants} places`
    : completionPct >= 80 ? `Quasi-complet sur ${maxApplicants} places`
    : completionPct >= 50 ? `Complétion attendue sur ${maxApplicants} places`
    : `Complétion limitée sur ${maxApplicants} places`

  const projectionToPct = (p: number) =>
    Math.max(0, Math.min(100, Math.round((projectAtBudget(p).applicants / maxApplicants) * 100)))

  const quickPresets = useMemo(() => {
    if (!reco) return []
    const recoPrice = smartReco || reco.recommended
    // Min ne doit jamais dépasser la reco (cohérence visuelle).
    const minPrice = Math.min(reco.min, recoPrice)
    return [
      { label: 'Min', value: minPrice, pct: projectionToPct(minPrice) },
      { label: 'Reco', value: recoPrice, accent: true as const, pct: projectionToPct(recoPrice) },
    ]
  }, [reco, smartReco, baseApplicants, maxApplicants])

  // Comparaison au marché : utile quand on est déjà à 100 % de complétion
  // (l'objectif devient "capter les meilleurs profils", pas "remplir").
  const marketMedian = marketMedianRaw
  const marketDeltaPct = useMemo(() => {
    if (!marketMedian || !hasValue) return null
    return Math.round(((numericValue - marketMedian) / marketMedian) * 100)
  }, [marketMedian, numericValue, hasValue])

  // Tag affiché à côté du KPI principal :
  // - Tant qu'on n'est pas plein → on parle de complétion (vs reco)
  // - Une fois plein → on parle d'attractivité (vs marché)
  const heroTag = useMemo(() => {
    if (completionPct >= 100) {
      if (marketDeltaPct == null) return null
      if (marketDeltaPct <= -10) return { label: `${marketDeltaPct} % vs marché`, tone: 'warn' as const }
      if (marketDeltaPct >= 10) return { label: `+${marketDeltaPct} % vs marché`, tone: 'good' as const }
      return { label: 'Aligné sur le marché', tone: 'good' as const }
    }
    if (!reco || !smartReco) return null
    // Comparaison vs la reco AFFICHÉE (smartReco) pour rester cohérent avec ce
    // que voit l'utilisateur, pas vs la reco brute du backend.
    const ratio = (projectedPrice - smartReco) / smartReco
    if (ratio > 0.05) return { label: `+${Math.round(ratio * 100)} % vs reco`, tone: 'good' as const }
    if (ratio < -0.1) return { label: `${Math.round(ratio * 100)} % vs reco`, tone: 'warn' as const }
    return null
  }, [completionPct, marketDeltaPct, projectedPrice, smartReco, reco])

  // Push target intelligent : la cible est TOUJOURS le smartReco (= prix qui
  // remplit le casting tout en restant aligné sur le marché). Le message s'adapte
  // au contexte : on parle de complétion si on peut encore remplir, sinon de
  // marché (qualité des profils).
  type PushTarget =
    | { kind: 'completion'; price: number; deltaPts: number; targetPct: number }
    | { kind: 'market'; price: number; gapPct: number }
  const pushTarget = useMemo<PushTarget | null>(() => {
    if (!reco || !smartReco || !currentProjection) return null
    if (!hasValue || numericValue >= smartReco) return null

    // Cas 1 : on peut encore gagner de la complétion en passant à smartReco.
    if (!supplyCapped && completionPct < 100) {
      const target = projectAtBudget(smartReco)
      const targetPct = Math.max(0, Math.min(100, Math.round((target.applicants / maxApplicants) * 100)))
      const deltaPts = Math.max(0, targetPct - completionPct)
      if (deltaPts >= 5) return { kind: 'completion', price: smartReco, deltaPts, targetPct }
    }

    // Cas 2 : la complétion ne bouge plus (vivier saturé ou déjà 100 %), mais on
    // est sous le marché → on pousse pour la qualité des profils.
    if (marketMedian && numericValue < marketMedian) {
      const gapPct = Math.abs(marketDeltaPct ?? 0)
      if (gapPct >= 5) return { kind: 'market', price: smartReco, gapPct }
    }

    return null
  }, [reco, smartReco, currentProjection, completionPct, projectedPrice, maxApplicants, marketMedian, hasValue, numericValue, marketDeltaPct, supplyCapped])

  const audienceSummary = useMemo(() => {
    const genderLabel =
      gender === 'female' ? 'Femmes'
      : gender === 'male' ? 'Hommes'
      : 'Tous genres'
    const ageLabel =
      age_min != null && age_max != null ? `${age_min}-${age_max} ans`
      : age_min != null ? `${age_min} ans et +`
      : age_max != null ? `Jusqu'à ${age_max} ans`
      : 'Tous âges'
    const formatLabel = (content_type || 'ugc').toUpperCase()
    return { genderLabel, ageLabel, formatLabel }
  }, [gender, age_min, age_max, content_type])

  // Statut audience qualitatif (sans exposer le ratio actifs/pool)
  const audienceStatus = useMemo(() => {
    const sup = reco?.audience_supply
    if (!sup || sup.eligible_pool == null || sup.eligible_pool < 50) return null
    if (sup.level === 'healthy') return { label: 'Audience large et réactive', tone: 'good' as const }
    if (sup.level === 'moderate') return { label: 'Audience qualifiée', tone: 'good' as const }
    if (sup.level === 'tight') return { label: 'Audience à activer', tone: 'warn' as const }
    return { label: 'Audience pointue', tone: 'warn' as const }
  }, [reco])

  // Anim flash sur les KPI à chaque changement
  const [kpiPulseKey, setKpiPulseKey] = useState(0)
  useEffect(() => {
    setKpiPulseKey((k) => k + 1)
  }, [completionPct, pulse?.active_count, audienceStatus?.label])

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <div className={styles.brand}>
          <div className={styles.logo}>
            <i className="fa-solid fa-wand-magic-sparkles" />
          </div>
          <div>
            <div className={styles.title}>Vogz Pricing Intelligence</div>
            <div className={styles.audienceInline}>
              <span key={`g-${audienceSummary.genderLabel}`} className={styles.audienceChip}>{audienceSummary.genderLabel}</span>
              <span key={`a-${audienceSummary.ageLabel}`} className={styles.audienceChip}>{audienceSummary.ageLabel}</span>
              <span key={`f-${audienceSummary.formatLabel}`} className={styles.audienceChip}>{audienceSummary.formatLabel}</span>
            </div>
          </div>
        </div>
        <div className={styles.live}>{loading ? 'Calcul…' : 'Live'}</div>
      </div>

      <div className={styles.cockpit}>
        <div className={styles.budgetCard}>
          <div className={styles.cardLabel}>
            <i className="fa-solid fa-coins" /> Votre budget par vidéo
          </div>

          <div className={styles.priceRow}>
            <div className={styles.priceInputWrap}>
              <input
                type="text"
                inputMode="numeric"
                placeholder={reco ? `${smartReco || reco.recommended}` : '...'}
                value={value == null ? '' : String(value)}
                onChange={(evt: any) => {
                  const clean = evt.target.value.replace(/[^0-9]/g, '')
                  onChange(clean)
                }}
              />
              <span className={styles.suffix}>€</span>
            </div>
            {reco && (
              <div className={styles.recoHint}>
                <span>Reco</span>
                <strong>{smartReco || reco.recommended} €</strong>
              </div>
            )}
          </div>

          <div className={styles.sliderWrap}>
            <input
              type="range"
              className={styles.slider}
              min={sliderRange.min}
              max={sliderRange.max}
              step={sliderRange.step}
              value={Math.max(sliderRange.min, Math.min(sliderRange.max, projectedPrice || smartReco || (reco?.recommended ?? sliderRange.min)))}
              onChange={(e) => onChange(String(Number(e.target.value)))}
              disabled={loading}
              style={
                reco
                  ? ({
                      ['--reco-pct' as any]: `${Math.max(0, Math.min(100, (((smartReco || reco.recommended) - sliderRange.min) / (sliderRange.max - sliderRange.min)) * 100))}%`,
                    } as any)
                  : undefined
              }
            />
            <div className={styles.sliderScale}>
              <span>{sliderRange.min} €</span>
              <span>{sliderRange.max} €</span>
            </div>
          </div>

          <div className={styles.presets}>
            {quickPresets.map((p) => {
              const cls = [
                styles.preset,
                hasValue && numericValue === p.value ? styles.presetActive : '',
                p.accent ? styles.presetAccent : '',
              ].filter(Boolean).join(' ')
              return (
                <button
                  type="button"
                  key={p.label}
                  className={cls}
                  onClick={() => onChange(String(p.value))}
                >
                  <span>{p.label}</span>
                  <strong>{p.value} €</strong>
                  {!supplyCapped && (
                    <em className={styles.presetPct}>{p.pct} %</em>
                  )}
                </button>
              )
            })}
          </div>

          <span className={styles.fees}>
            {t('form-casting:casting.cards-remuneration.fees', 'Hors commission et TVA')}
          </span>
        </div>

        <div className={styles.impactCard}>
          <div className={styles.cardLabel}>
            <i className="fa-solid fa-chart-line" /> Impact estimé en live
          </div>

          <div key={`kpi-${kpiPulseKey}`} className={styles.kpiHero}>
            {supplyCapped ? (
              <>
                <div className={`${styles.kpiValue} ${styles.kpiTone_mid}`}>
                  {loading ? <span className={styles.kpiSkeleton} /> : <>≈{projectedApplicants}</>}
                </div>
                <div className={styles.kpiLabel}>
                  <span>candidatures qualifiées attendues</span>
                  <em className={styles.kpiWarn}>Vivier ciblé sur cette audience</em>
                </div>
              </>
            ) : (
              <>
                <div className={`${styles.kpiValue} ${styles[`kpiTone_${completionTone}`]}`}>
                  {loading ? <span className={styles.kpiSkeleton} /> : <>{completionPct}<small>%</small></>}
                </div>
                <div className={styles.kpiLabel}>
                  <span>{completionLabel}</span>
                  {heroTag && (
                    <em className={heroTag.tone === 'good' ? styles.kpiBoost : styles.kpiWarn}>{heroTag.label}</em>
                  )}
                </div>
              </>
            )}
          </div>

          {!supplyCapped && (
            <div className={styles.completionBar}>
              <div
                className={`${styles.completionFill} ${styles[`completionFill_${completionTone}`]}`}
                style={{ width: `${loading ? 0 : completionPct}%` }}
              />
            </div>
          )}

          <div className={styles.kpiList}>
            {pulse && pulse.active_count > 0 && pulse.median != null && (
              <div className={styles.kpiItem}>
                <span className={styles.kpiDot} />
                <div>
                  Marché concurrentiel : médiane <strong>{pulse.median} €</strong> sur {pulse.active_count} casting{pulse.active_count > 1 ? 's' : ''} actif{pulse.active_count > 1 ? 's' : ''}
                </div>
                {marketDeltaPct != null && marketDeltaPct <= -10 && (
                  <span className={styles.kpiWarn}>{marketDeltaPct} % vs marché</span>
                )}
                {marketDeltaPct != null && marketDeltaPct >= 10 && (
                  <span className={styles.kpiBoost}>+{marketDeltaPct} % vs marché</span>
                )}
              </div>
            )}

            {pulse && pulse.active_count === 0 && (
              <div className={styles.kpiItem}>
                <span className={styles.kpiDot} />
                <div>Aucun concurrent actif sur cette audience — terrain libre.</div>
              </div>
            )}

            {audienceStatus && (
              <div className={styles.kpiItem}>
                <i className={`fa-solid ${audienceStatus.tone === 'good' ? 'fa-circle-check' : 'fa-users-viewfinder'} ${audienceStatus.tone === 'good' ? styles.kpiIconGood : styles.kpiIconWarn}`} />
                <div>{audienceStatus.label}</div>
              </div>
            )}
          </div>

          {pushTarget && pushTarget.kind === 'completion' && (
            <button
              type="button"
              className={styles.inlineBoost}
              onClick={() => onChange(String(pushTarget.price))}
            >
              <i className="fa-solid fa-arrow-trend-up" />
              <span>
                Passez à <strong>{pushTarget.price} €</strong>
                {pushTarget.targetPct >= 100
                  ? <> pour viser <strong>100 % de complétion</strong></>
                  : <> pour gagner <strong>+{pushTarget.deltaPts} pts</strong> de complétion</>}
              </span>
              <span className={styles.inlineBoostCta}>Appliquer</span>
            </button>
          )}

          {pushTarget && pushTarget.kind === 'market' && (
            <button
              type="button"
              className={styles.inlineBoost}
              onClick={() => onChange(String(pushTarget.price))}
            >
              <i className="fa-solid fa-bolt" />
              <span>
                Vous êtes <strong>−{pushTarget.gapPct} %</strong> sous le marché. Passez à <strong>{pushTarget.price} €</strong> pour capter les meilleurs profils.
              </span>
              <span className={styles.inlineBoostCta}>S'aligner</span>
            </button>
          )}

          {belowFloor && (
            <div className={styles.floorInline}>
              <i className="fa-solid fa-triangle-exclamation" />
              Minimum {reco!.floor} € recommandé pour attirer les profils qualifiés.
            </div>
          )}
        </div>
      </div>

      {reco?.audience_suggestions && reco.audience_suggestions.length > 0 && completionPct < 100 && (
        <div className={styles.suggestRow}>
          <span className={styles.suggestLabel}>Élargir l'audience :</span>
          <div className={styles.suggestList}>
            {reco.audience_suggestions.slice(0, 3).map((s) => {
              const projectedPct = s.expected_applicants != null
                ? Math.max(0, Math.min(100, Math.round((s.expected_applicants / maxApplicants) * 100)))
                : null
              const deltaPts = projectedPct != null ? Math.max(0, projectedPct - completionPct) : 0
              return (
                <button
                  type="button"
                  key={s.label}
                  className={styles.suggestBtn}
                  onClick={() => handleAudienceExpand(s)}
                >
                  <strong>{s.label}</strong>
                  {projectedPct != null && deltaPts >= 5
                    ? <em>+{deltaPts} pts de complétion → {projectedPct}%</em>
                    : <em>+{s.delta_pool.toLocaleString('fr-FR')} créateurs accessibles</em>}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default observer(SmartPricingWidget)
